import type {
  TPerformancePracticeState,
  TPerformancePracticeResult,
  TWorkBehaviorResult,
  TWorkBehaviorState,
} from 'types/kinerja-pelaksanaan'
import type { CommonListResponse } from 'types/api'
import type { CommonMultiPeriodMeta } from 'types/kinerja'
import type { MultiPeriodPayload } from 'types/kinerja'
import type { TStatePelaksanaan } from 'types/kinerja-pelaksanaan'
import type { CommonResponse } from 'types/api'
import type { IReflectionImprovementTypes } from 'types/kinerja-pelaksanaan'
import type { IFollowUpTargetItem } from 'types/kinerja-pelaksanaan'

// ------------------------------------------
// Perencanaan List
// ------------------------------------------
export type ParamsApiGetPerencanaanList = Partial<MultiPeriodPayload> & {
  isMetaOnly?: boolean
  states?: Array<KinerjaStatusGpk | 'ALL'>
}

export type ResponseApiGetPerencanaanList = CommonListResponse<
  PerencanaanItem,
  CommonMultiPeriodMeta & {
    states: Array<KinerjaStatusGpk | 'ALL'>
    totalCount: Record<KinerjaStatusGpk | 'ALL', number>
  }
>

export type PerencanaanItem = {
  id: string
  nama: string
  state: KinerjaStatusGpk
  ptkId: string
}

// ------------------------------------------
// School List
// ------------------------------------------
export type ResponseApiGetSchoolList = CommonListResponse<SchoolItem, {}>

export type SchoolItem = {
  npsn: string
  schoolName: string
}

// ------------------------------------------
// Pelaksanaan Guru List
// ------------------------------------------
export type ParamsApiGetPelaksanaanList = MultiPeriodPayload

export type ResponseApiGetPelaksanaanList = CommonListResponse<
  PelaksanaanItem,
  ResponseApiGetPelaksanaanListMeta
>

export type ResponseApiGetPelaksanaanListMeta = {
  total: number
  completed: number
  states: { [key in GpkPraktikKinerjaGradingStateEnum]: number }
}

export type PelaksanaanItem = {
  userId: string
  ptkId: string
  name: string
  observation: PelaksanaanObservation
  workBehavior: PelaksanaanWorkBehavior
}

export type PelaksanaanObservation = {
  date: string
  gradingState: GpkPraktikKinerjaGradingStateEnum
  progress: number
  state: TStatePelaksanaan
  total: number
}

// Note: the API returns grading state value in a raw string (with space)
// We need to map it to the enum value for consistency and better readability
export enum GpkPraktikKinerjaGradingStateEnum {
  OBSERVASI = 'Rating observasi',
  NILAI_TINDAK_LANJUT = 'Nilai tindak lanjut',
  NILAI_REFLEKSI_TINDAK_LANJUT = 'Nilai refleksi tindak lanjut',
  SELESAI = 'Sudah selesai',
}

export type PelaksanaanWorkBehavior = {
  gradingState: GpkPerilakuKerjaGradingStateEnum
  progress: number
  result: TWorkBehaviorResult
  state: TWorkBehaviorState
  total: number
}

// Note: the API returns grading state value in a raw string (with space)
// We need to map it to the enum value for consistency and better readability
export enum GpkPerilakuKerjaGradingStateEnum {
  BELUM_DINILAI = 'Belum dinilai',
  SELESAI = 'Sudah dinilai',
}

export type PelaksanaanPerformancePractice = {
  state: TPerformancePracticeState
  result: TPerformancePracticeResult
}

// ------------------------------------------
// Rencana Tindak Lanjut
// ------------------------------------------
export type ParamsApiGetRencanaTindakLanjut = MultiPeriodPayload & {
  ptkId?: string
}
export type ResponseApiGetRencanaTindakLanjut = CommonResponse<
  RencanaTindakLanjutData,
  {}
>

export type RencanaTindakLanjutData = {
  state: RencanaTindakLanjutState
  detail: {
    selfReflection: string
    additionalFollowUpTarget: string
    additionalFollowUpEffort: string
    additionalFollowUpPeriod: string
    additionalSupportNeed: string
    followUpTargets: Array<IFollowUpTargetItem>
    supportNeed: string
  }
}

export type RencanaTindakLanjutState = GpkPraktikKinerjaFormState

export type ParamsUpsertRencanaTindakLanjut = MultiPeriodPayload & {
  tipe: 'GPK'
  state: RencanaTindakLanjutState
  detail: {
    selfReflection: string
    additionalFollowUpTarget: string
    additionalFollowUpEffort: string
    additionalFollowUpPeriod: string
    additionalSupportNeed: string
    followUpTargets?: Array<{ id: string; learningModuleId: string }>
    supportNeed?: string
  }
}

// ------------------------------------------
// Pemantauan Rencana Tindak Lanjut
// ------------------------------------------
export type ParamsApiGetPemantauanRencanaTindakLanjut = MultiPeriodPayload & {
  ptkId?: string
}

export type ResponseApiGetPemantauanRencanaTindakLanjut = CommonResponse<
  PemantauanRencanaTindakLanjutData,
  {}
>

export type PemantauanRencanaTindakLanjutData = {
  state: PemantauanRencanaTindakLanjutState
  detail: {
    reflectionImprovement: IReflectionImprovementTypes
    reflectionNotes: string
  }
}

export type PemantauanRencanaTindakLanjutState = GpkPraktikKinerjaFormState

export type ParamsUpsertPemantauanRencanaTindakLanjut =
  PemantauanRencanaTindakLanjutData &
    MultiPeriodPayload & {
      tipe: 'GPK'
    }

// -------------------------------------
// Miscellaneous Types
// -------------------------------------

export type KinerjaStatusGpk =
  | 'NOT_FOUND'
  | 'DRAFT'
  | 'WAITING_APPROVAL'
  | 'REVISION_TK'
  | 'REVISION_PPK'
  | 'WAITING_APPROVAL_PPK'
  | 'READY'
  // @deprecated: use READY instead
  | 'APPROVED'

export type GpkPraktikKinerjaFormState = 'SUBMITTED' | 'DRAFT' | 'NOT_CREATED'
